<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLotteryStore } from '../stores/lottery.js'
import { useUsersStore } from '../stores/users.js'
import { useOrgStore } from '../stores/orgStore.js'
import { usePermissions } from '../utils/permissions.js'
import {
  fetchLotteryAttachments,
  uploadLotteryAttachment,
  downloadLotteryAttachment,
  sendDeadlineReminder,
} from '../services/lotteryService.js'
import AssignmentTable from '../components/AssignmentTable.vue'
import TicketGrid from '../components/TicketGrid.vue'
import UploadForm from '../components/UploadForm.vue'
import DrawPanel from '../components/DrawPanel.vue'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import LotterySkeletonCard from '../components/LotterySkeletonCard.vue'
import Skeleton from '../components/ui/Skeleton.vue'
import LifecycleStatusBadge from '../components/LifecycleStatusBadge.vue'
import LotteryIntegritySection from '../components/LotteryIntegritySection.vue'
import LotteryApiOpsPanel from '../components/LotteryApiOpsPanel.vue'
import { useTicketOpsStore } from '../stores/ticketOpsStore.js'
import { getLifecycleStatusMeta } from '../utils/adminUi.js'
import { parseActionError } from '../utils/adminUi.js'
import {
  buildTicketIntegrityWarnings,
  buildTicketLifecycleRecords,
  canAssignTicketsForLotteryStatus,
  canTransitionTicketState,
  getTicketLifecycleMeta,
  parseTicketOperationError,
  summarizeTicketLifecycle,
} from '../utils/ticketUi.js'
import { DEFAULT_ADMIN_PAGE_SIZE, paginateRows, toPositiveInt } from '../utils/operationsUi.js'
import {
  getLotteryLifecycleMeta,
  groupWinnersForDisplay,
  isLotteryReadOnly,
  normalizeDrawHistory,
  normalizeLotteryStatus,
} from '../utils/lotteryUi.js'
import { downloadCsv, getRecentExportHistory, recordExportEvent, toCsv, toLifecycleFilename } from '../utils/exportUi.js'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const lotteryStore = useLotteryStore()
const usersStore = useUsersStore()
const orgStore = useOrgStore()
const ticketOpsStore = useTicketOpsStore()
const { can } = usePermissions(authStore, orgStore)

const lotteryId = computed(() => route.params.id)
const isManagerOrAdmin = computed(() => can.assignUsers.value)
const pageLoading = ref(true)
const ticketFilter = ref('all')
const ticketPage = ref(1)
const ticketPageSize = ref(DEFAULT_ADMIN_PAGE_SIZE)
const selectedTicketNumbers = ref([])
const ticketInputValue = ref('')
const ticketOperationMessage = ref('')
const ticketOperationError = ref('')
const pendingTicketAction = ref(null)
const pendingTicketActionLoading = ref(false)
const assignmentRowLocks = ref({})
const exportDialogOpen = ref(false)
const exportScope = ref('execution')
const exportLoading = ref(false)
const exportError = ref('')
const exportHistory = ref([])

const selectedTickets = ref([])
const selectedUserIds = ref([])
const selectedManagerIds = ref([])

const assignUsersLoading = ref(false)
const assignUsersError = ref('')
const assignUsersMessage = ref('')

const assignManagersLoading = ref(false)
const assignManagersError = ref('')
const assignManagersMessage = ref('')
const latestUserBatch = ref(null)
const latestUserBatchImageUrl = ref('')
const latestUserBatchImageLoading = ref(false)
const latestUserBatchImageError = ref('')
const attachments = ref([])
const attachmentsLoading = ref(false)
const attachmentsError = ref('')
const attachmentUploadLoading = ref(false)
const attachmentUploadError = ref('')
const attachmentUploadSuccess = ref('')
const deadlineReminderLoading = ref(false)
const deadlineReminderError = ref('')
const deadlineReminderMessage = ref('')
const replaceExistingAttachment = ref(false)
const selectedAttachmentFile = ref(null)
const submissionCacheKey = computed(() => {
  const identity = String(authStore.userId ?? '').trim().toLowerCase()
  return `latest_submission:${lotteryId.value}:${identity}`
})

const assignedIds = computed(() => {
  return new Set((lotteryStore.assignments || []).map((item) => Number(item.user_id)))
})

const selectedSubOrgId = computed(() => {
  const uiValue = Number(orgStore.activeSubOrgId)
  return Number.isInteger(uiValue) && uiValue > 0 ? uiValue : null
})

const resolveUserIdentity = (userId, fallbackUsername = '') => {
  if (typeof usersStore.resolveUserIdentity === 'function') {
    return usersStore.resolveUserIdentity(userId, fallbackUsername)
  }
  const normalizedId = Number(userId)
  const label = String(fallbackUsername || '').trim() || (Number.isInteger(normalizedId) && normalizedId > 0 ? `User #${normalizedId}` : 'Unknown user')
  return {
    id: Number.isInteger(normalizedId) && normalizedId > 0 ? normalizedId : null,
    username: String(fallbackUsername || '').trim(),
    fullName: '',
    phone: '',
    displayName: label,
    found: false,
  }
}

const toIdentityLabel = (identity) => {
  if (!identity) return 'Unknown user'
  const base = identity.displayName || identity.username || 'Unknown user'
  return identity.phone ? `${base} (${identity.phone})` : base
}

const enrichedAssignments = computed(() => {
  return (lotteryStore.assignments || []).map((assignment) => {
    const identity = resolveUserIdentity(assignment?.user_id, assignment?.username)
    return {
      ...assignment,
      username: assignment?.username || identity.username || identity.displayName,
      display_name: identity.displayName,
      display_phone: identity.phone,
    }
  })
})

const assignableUsers = computed(() => usersStore.users || [])
const lotteryStatus = computed(() => normalizeLotteryStatus(lotteryStore.currentLottery))
const lotteryStatusMeta = computed(() => getLotteryLifecycleMeta(lotteryStore.currentLottery))
const lotteryReadOnly = computed(() => isLotteryReadOnly(lotteryStore.currentLottery))
const executionHistory = computed(() => normalizeDrawHistory(lotteryStore.drawHistory))
const ticketLifecycleRows = computed(() => {
  return buildTicketLifecycleRecords({
    history: ticketOpsStore.ticketHistory,
    assignments: lotteryStore.assignments,
    batches: lotteryStore.batches,
    drawHistory: lotteryStore.drawHistory,
  })
})
const filteredTicketRows = computed(() => {
  if (ticketFilter.value === 'all') return ticketLifecycleRows.value
  return ticketLifecycleRows.value.filter((row) => row.state === ticketFilter.value)
})
const paginatedTicketRows = computed(() => paginateRows(filteredTicketRows.value, ticketPage.value, ticketPageSize.value))
const visibleTicketRows = computed(() => paginatedTicketRows.value.items)
const selectedTicketSet = computed(() => new Set(selectedTicketNumbers.value))
const ticketSummary = computed(() => summarizeTicketLifecycle(ticketLifecycleRows.value))
const ticketIntegrity = computed(() => buildTicketIntegrityWarnings(ticketLifecycleRows.value))
const ticketBulkBusy = computed(() => pendingTicketActionLoading.value || ticketOpsStore.operationLoading)
const assignmentMutationActive = computed(() => assignUsersLoading.value || assignManagersLoading.value)
const ticketPriceValue = computed(() => {
  const raw = lotteryStore.currentLottery?.ticket_price
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
})
const ticketPriceDisplay = computed(() => ticketPriceValue.value.toFixed(2))
const soldTicketsForValue = computed(() => Number(ticketSummary.value?.sold || 0) + Number(ticketSummary.value?.winner || 0))
const totalLotteryValue = computed(() => ticketPriceValue.value * soldTicketsForValue.value)
const totalLotteryValueDisplay = computed(() => totalLotteryValue.value.toFixed(2))
const structuredIntegrityWarnings = computed(() => {
  return (ticketIntegrity.value.warnings || []).map((message) => ({
    severity: 'warning',
    source: 'ticket_integrity',
    recoverable: true,
    retryable: true,
    correlation_ref: '',
    message,
  }))
})

const openExportDialog = (scope) => {
  exportScope.value = scope
  exportError.value = ''
  exportDialogOpen.value = true
}

const closeExportDialog = () => {
  if (exportLoading.value) return
  exportDialogOpen.value = false
}

const exportRowsByScope = computed(() => {
  if (exportScope.value === 'winners') {
    return latestWinnerRows.value.map((row) => ({
      winner: toWinnerDisplayName(row),
      ticket: toTicketDisplayValue(row.ticketNumber),
      validation: row.duplicate ? 'duplicate' : row.invalid ? 'invalid' : 'ok',
    }))
  }
  if (exportScope.value === 'tickets') {
    return ticketLifecycleRows.value.map((row) => ({
      ticket: row.ticketNumber,
      state: row.state,
      owner_user_id: row.ownerUserId ?? '',
      history_events: row.history?.length || 0,
    }))
  }
  return executionTimeline.value.map((row) => ({
    executed_at: row.executedAt || '',
    status: row.statusLabel,
    winner_count: row.winnerCount ?? '',
    notes: row.executionNotes || '',
  }))
})

const exportDetailCsv = async () => {
  if (!exportRowsByScope.value.length || exportLoading.value) return
  exportLoading.value = true
  exportError.value = ''
  try {
    const headers = Object.keys(exportRowsByScope.value[0] || {}).map((key) => ({ key, label: key }))
    const csv = toCsv(exportRowsByScope.value, headers)
    const filename = toLifecycleFilename({
      scope: `lottery-${lotteryId.value}-${exportScope.value}`,
      lifecycle: lotteryStatus.value,
    })
    downloadCsv(filename, csv)
    recordExportEvent({
      exported_by: authStore.user?.username || authStore.user?.email || 'unknown',
      export_scope: `lottery_${exportScope.value}`,
      partial_export_warning: false,
      record_count: exportRowsByScope.value.length,
    })
    exportHistory.value = getRecentExportHistory()
    exportDialogOpen.value = false
  } catch {
    exportError.value = 'Failed to export lottery report.'
  } finally {
    exportLoading.value = false
  }
}

const latestDrawResult = computed(() => {
  return executionHistory.value[0] ?? null
})

const executionTimeline = computed(() => {
  return executionHistory.value.map((entry, index) => {
    const status = String(entry?.executionStatus ?? '').trim().toLowerCase()
    const isRecoverable = status === 'partial' || status === 'recoverable' || status === 'failed'
    return {
      ...entry,
      isLatest: index === 0,
      statusLabel: status,
      isRecoverable,
    }
  })
})

const latestExecutionIntegrity = computed(() => {
  const latest = latestDrawResult.value
  return latest?.integrity ?? null
})

const integritySignals = computed(() => {
  const integrity = latestExecutionIntegrity.value
  if (!integrity) return []

  return [
    {
      key: 'tickets',
      label: 'Total tickets included',
      value: integrity.totalTickets ?? 'Unknown',
      state: integrity.totalTickets === null ? 'missing' : 'ok',
    },
    {
      key: 'participants',
      label: 'Eligible participants',
      value: integrity.eligibleParticipants ?? 'Unknown',
      state: integrity.eligibleParticipants === null ? 'missing' : 'ok',
    },
    {
      key: 'excluded',
      label: 'Excluded/refunded',
      value: integrity.excludedOrRefunded ?? 'Unknown',
      state: integrity.excludedOrRefunded === null ? 'missing' : 'ok',
    },
    {
      key: 'audit',
      label: 'Audit reference IDs',
      value: integrity.auditReferenceIds?.length ? integrity.auditReferenceIds.join(', ') : 'Not provided',
      state: integrity.auditReferenceIds?.length ? 'ok' : 'missing',
    },
  ]
})

const latestWinnerRows = computed(() => groupWinnersForDisplay(latestDrawResult.value?.winners ?? []))

const toWinnerDisplayName = (winner) => {
  const userId = winner?.userId ?? winner?.winner_user_id ?? winner?.record?.winner_user_id
  const identity = resolveUserIdentity(userId, winner?.username)
  return toIdentityLabel(identity)
}

const toTicketDisplayValue = (ticket) => {
  const parsed = Number(ticket)
  return Number.isInteger(parsed) && parsed >= 0 ? String(parsed) : 'Missing'
}

const winnerLifecycleMeta = (winner) => {
  const deleted = winner?.record?.is_deleted === true || winner?.record?.user_deleted === true
  return getLifecycleStatusMeta(deleted)
}

const onSelectedTickets = (tickets) => {
  selectedTickets.value = Array.isArray(tickets) ? tickets : []
}

const toTicketOwnerLabel = (ownerUserId) => {
  const identity = resolveUserIdentity(ownerUserId)
  return toIdentityLabel(identity)
}

const parseApiError = (err, fallback) => {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback
  }
  return fallback
}

const formatDeadline = (value) => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

const getAttachmentId = (attachment) => attachment?.id ?? attachment?.attachment_id
const getAttachmentFilename = (attachment) => attachment?.filename ?? attachment?.name ?? 'attachment'
const getAttachmentCreatedAt = (attachment) => attachment?.created_at ?? attachment?.uploaded_at ?? ''
const getAttachmentUploadedBy = (attachment) => {
  return attachment?.uploaded_by_username ?? attachment?.uploaded_by?.username ?? attachment?.uploaded_by ?? 'Unknown'
}

const loadAttachments = async () => {
  attachmentsLoading.value = true
  attachmentsError.value = ''

  try {
    const data = await fetchLotteryAttachments(lotteryId.value)
    attachments.value = Array.isArray(data) ? data : []
  } catch (err) {
    attachments.value = []
    attachmentsError.value = parseApiError(err, 'Failed to load attachments.')
  } finally {
    attachmentsLoading.value = false
  }
}

const isAllowedAttachmentFile = (file) => {
  if (!file?.name) return false
  const name = String(file.name).toLowerCase()
  return name.endsWith('.pdf') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')
}

const onAttachmentFileSelected = (event) => {
  selectedAttachmentFile.value = event?.target?.files?.[0] ?? null
  attachmentUploadError.value = ''
  attachmentUploadSuccess.value = ''
}

const uploadAttachment = async () => {
  attachmentUploadError.value = ''
  attachmentUploadSuccess.value = ''

  if (!selectedAttachmentFile.value) {
    attachmentUploadError.value = 'Select a file first.'
    return
  }

  if (!isAllowedAttachmentFile(selectedAttachmentFile.value)) {
    attachmentUploadError.value = 'Only PDF, JPG, and PNG files are allowed.'
    return
  }

  attachmentUploadLoading.value = true
  try {
    await uploadLotteryAttachment(lotteryId.value, selectedAttachmentFile.value, replaceExistingAttachment.value)
    attachmentUploadSuccess.value = 'Attachment uploaded.'
    selectedAttachmentFile.value = null
    await loadAttachments()
  } catch (err) {
    attachmentUploadError.value = parseApiError(err, 'Failed to upload attachment.')
  } finally {
    attachmentUploadLoading.value = false
  }
}

const triggerDeadlineReminder = async () => {
  deadlineReminderLoading.value = true
  deadlineReminderError.value = ''
  deadlineReminderMessage.value = ''

  try {
    const result = await sendDeadlineReminder(lotteryId.value)
    const sent = Number(result?.sent ?? 0)
    const failed = Number(result?.failed ?? 0)
    deadlineReminderMessage.value = `Reminder sent: ${sent}, failed: ${failed}.`
  } catch (err) {
    deadlineReminderError.value = parseApiError(err, 'Failed to send deadline reminder.')
  } finally {
    deadlineReminderLoading.value = false
  }
}

const downloadAttachment = async (attachment) => {
  const attachmentId = getAttachmentId(attachment)
  if (!attachmentId) return

  try {
    const blob = await downloadLotteryAttachment(lotteryId.value, attachmentId)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = getAttachmentFilename(attachment)
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    attachmentsError.value = parseApiError(err, 'Failed to download attachment.')
  }
}

const releaseLatestUserBatchImage = () => {
  if (latestUserBatchImageUrl.value) {
    URL.revokeObjectURL(latestUserBatchImageUrl.value)
    latestUserBatchImageUrl.value = ''
  }
}

const readSubmissionCache = () => {
  try {
    const raw = localStorage.getItem(submissionCacheKey.value)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed?.tickets)
      ? parsed.tickets
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
      : []
  } catch (err) {
    return []
  }
}

const writeSubmissionCache = (tickets) => {
  const normalized = Array.isArray(tickets)
    ? [...new Set(tickets.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0))]
    : []
  localStorage.setItem(
    submissionCacheKey.value,
    JSON.stringify({
      tickets: normalized,
      saved_at: new Date().toISOString(),
    }),
  )
}

const normalizeBatchTicketsToLocal = (batch) => {
  if (!batch) return []
  const maxTickets = Number(lotteryStore.currentLottery?.tickets_per_batch) || 0
  const globalStart = Number(batch.global_start)
  const globalEnd = Number(batch.global_end)
  const hasGlobalRange = Number.isFinite(globalStart) && Number.isFinite(globalEnd) && globalEnd >= globalStart
  const rawTickets = Array.isArray(batch.sold_tickets) ? batch.sold_tickets : []

  const localized = rawTickets
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value >= 1)
    .map((value) => {
      if (hasGlobalRange && value >= globalStart && value <= globalEnd) {
        return value - globalStart + 1
      }
      return value
    })
    .filter((value) => value >= 1 && (maxTickets <= 0 || value <= maxTickets))

  return [...new Set(localized)].sort((a, b) => a - b)
}

const loadLatestUserBatchImage = async (batch) => {
  latestUserBatchImageLoading.value = true
  latestUserBatchImageError.value = ''
  releaseLatestUserBatchImage()

  try {
    const response = await lotteryStore.fetchBatchImage(lotteryId.value, batch.id)
    if (!response?.success) {
      latestUserBatchImageError.value = response?.error ?? 'Could not load uploaded image.'
      return
    }

    if (!response?.data || response.data.size === 0) {
      latestUserBatchImageError.value = 'Uploaded image is empty.'
      return
    }
    latestUserBatchImageUrl.value = URL.createObjectURL(response.data)
  } catch (err) {
    latestUserBatchImageError.value = err?.response?.data?.detail ?? 'Could not load uploaded image.'
  } finally {
    latestUserBatchImageLoading.value = false
  }
}

const loadLatestUserBatch = async () => {
  latestUserBatch.value = null
  latestUserBatchImageError.value = ''
  releaseLatestUserBatchImage()

  let result = { success: false, data: [] }

  if (typeof lotteryStore.fetchMyBatches === 'function') {
    result = await lotteryStore.fetchMyBatches(lotteryId.value)
  } else if (typeof lotteryStore.fetchUserBatches === 'function') {
    const numericUserId = Number(authStore.userId)
    if (Number.isInteger(numericUserId) && numericUserId > 0) {
      result = await lotteryStore.fetchUserBatches(lotteryId.value, numericUserId)
    }
  }

  if (!result?.success || !result.data?.length) {
    // Fall back to locally cached tickets if server call failed or no batches yet
    selectedTickets.value = readSubmissionCache()
    return
  }

  const latest = [...result.data].sort((a, b) => {
    const tsA = Date.parse(a?.created_at || '')
    const tsB = Date.parse(b?.created_at || '')
    if (Number.isFinite(tsA) && Number.isFinite(tsB) && tsA !== tsB) {
      return tsB - tsA
    }
    return Number(b?.id || 0) - Number(a?.id || 0)
  })[0]

  latestUserBatch.value = latest
  selectedTickets.value = normalizeBatchTicketsToLocal(latest)
  writeSubmissionCache(selectedTickets.value)
  await loadLatestUserBatchImage(latest)
}

const handleUserUploadSuccess = async () => {
  writeSubmissionCache(selectedTickets.value)
  await loadLatestUserBatch()
}

const toggleSelection = (target, id, checked) => {
  const numericId = Number(id)
  if (checked) {
    if (!target.includes(numericId)) {
      target.push(numericId)
    }
    return
  }
  const index = target.indexOf(numericId)
  if (index >= 0) {
    target.splice(index, 1)
  }
}

const isUserDeleted = (user) => {
  return user?.is_deleted === true || user?.deleted === true || user?.user_deleted === true
}

const isUserAssignable = (user) => {
  if (!user || isUserDeleted(user)) return false
  if (user?.organization_deleted === true || user?.sub_org_deleted === true) return false
  return true
}

const setAssignmentRowLocked = (userId, locked) => {
  const key = String(userId)
  assignmentRowLocks.value = {
    ...assignmentRowLocks.value,
    [key]: Boolean(locked),
  }
}

const isAssignmentRowLocked = (userId) => Boolean(assignmentRowLocks.value[String(userId)])

const toTicketNumbersFromInput = (value) => {
  const input = String(value || '').trim()
  if (!input) return []
  const parts = input.split(',').map((part) => part.trim()).filter(Boolean)
  const numbers = new Set()

  for (const part of parts) {
    if (part.includes('-')) {
      const [startRaw, endRaw] = part.split('-').map((token) => Number(token.trim()))
      if (!Number.isInteger(startRaw) || !Number.isInteger(endRaw) || startRaw <= 0 || endRaw < startRaw) continue
      for (let current = startRaw; current <= endRaw; current += 1) numbers.add(current)
      continue
    }
    const single = Number(part)
    if (Number.isInteger(single) && single > 0) numbers.add(single)
  }

  return [...numbers].sort((a, b) => a - b)
}

const syncTicketSelectionFromInput = () => {
  selectedTicketNumbers.value = toTicketNumbersFromInput(ticketInputValue.value)
}

const toggleTicketSelection = (ticketNumber, checked) => {
  const numeric = Number(ticketNumber)
  if (!Number.isInteger(numeric) || numeric <= 0) return
  const next = new Set(selectedTicketNumbers.value)
  if (checked) next.add(numeric)
  else next.delete(numeric)
  selectedTicketNumbers.value = [...next].sort((a, b) => a - b)
}

const selectFilteredTickets = () => {
  selectedTicketNumbers.value = filteredTicketRows.value.map((row) => row.ticketNumber)
  ticketInputValue.value = selectedTicketNumbers.value.join(',')
}

const updateTicketPageSize = (nextPageSize) => {
  ticketPageSize.value = toPositiveInt(nextPageSize, DEFAULT_ADMIN_PAGE_SIZE)
  ticketPage.value = 1
}

const clearTicketSelection = () => {
  selectedTicketNumbers.value = []
  ticketInputValue.value = ''
}

const openTicketActionDialog = (action) => {
  ticketOperationError.value = ''
  ticketOperationMessage.value = ''

  if (!selectedTicketNumbers.value.length) {
    ticketOperationError.value = 'Select at least one ticket before running a bulk operation.'
    return
  }

  const stateByTicket = new Map(ticketLifecycleRows.value.map((row) => [row.ticketNumber, row.state]))
  const invalidTargets = selectedTicketNumbers.value.filter((ticketNumber) => {
    const fromState = stateByTicket.get(ticketNumber) || 'available'
    return !canTransitionTicketState(fromState, action)
  })

  if (invalidTargets.length) {
    ticketOperationError.value = `Operation blocked for ${invalidTargets.length} ticket(s) due to lifecycle restrictions.`
    return
  }

  pendingTicketAction.value = {
    action,
    ticketNumbers: [...selectedTicketNumbers.value],
  }
}

const closeTicketActionDialog = () => {
  if (pendingTicketActionLoading.value) return
  pendingTicketAction.value = null
}

const ticketActionTitle = computed(() => {
  const action = pendingTicketAction.value?.action
  if (action === 'reserved') return 'Reserve Tickets'
  if (action === 'assigned') return 'Release Reservation'
  if (action === 'sold') return 'Mark Tickets as Sold'
  return 'Confirm Ticket Operation'
})

const ticketActionTone = computed(() => {
  const action = pendingTicketAction.value?.action
  if (action === 'sold') return 'success'
  return 'warning'
})

const ticketActionMessage = computed(() => {
  const count = pendingTicketAction.value?.ticketNumbers?.length || 0
  if (count <= 0) return ''
  return `This operation will update ${count} ticket(s). Changes are audit-tracked and may be partially applied.`
})

const confirmTicketAction = async () => {
  if (!pendingTicketAction.value || pendingTicketActionLoading.value) return

  pendingTicketActionLoading.value = true
  ticketOperationError.value = ''
  ticketOperationMessage.value = ''
  try {
    const result = await ticketOpsStore.performOperation(
      lotteryId.value,
      pendingTicketAction.value.action,
      pendingTicketAction.value.ticketNumbers,
    )

    if (!result.success) {
      ticketOperationError.value = result.partial
        ? result.error
        : parseTicketOperationError(result.error, 'Ticket operation failed.')
      return
    }

    ticketOperationMessage.value = `${pendingTicketAction.value.ticketNumbers.length} ticket(s) updated successfully.`
    await Promise.all([
      lotteryStore.fetchAssignments(lotteryId.value),
      lotteryStore.fetchBatches(lotteryId.value),
      lotteryStore.fetchDrawHistory(lotteryId.value),
      ticketOpsStore.fetchTicketHistory(lotteryId.value),
    ])
    clearTicketSelection()
    pendingTicketAction.value = null
  } finally {
    pendingTicketActionLoading.value = false
  }
}

const lifecycleAwareAssignmentError = (err, fallback) => {
  return parseActionError(err, fallback, {
    conflict: 'Assignment state changed on the server. Refresh and retry.',
    forbidden: 'You do not have permission to change assignments in this lottery.',
    notFound: 'Assignment target no longer exists. Refresh and retry.',
  })
}

const toggleUserSelection = (id, checked) => {
  toggleSelection(selectedUserIds.value, id, checked)
}

const toggleManagerSelection = (id, checked) => {
  toggleSelection(selectedManagerIds.value, id, checked)
}

const loadManagerData = async () => {
  await Promise.all([
    lotteryStore.fetchAssignments(lotteryId.value),
    lotteryStore.fetchBatches(lotteryId.value),
    lotteryStore.fetchDrawHistory(lotteryId.value),
    ticketOpsStore.fetchTicketHistory(lotteryId.value),
    usersStore.fetchUsers(),
  ])
}

const assignUsers = async () => {
  if (assignmentMutationActive.value) return
  assignUsersLoading.value = true
  assignUsersError.value = ''
  assignUsersMessage.value = ''

  if (!canAssignTicketsForLotteryStatus(lotteryStatus.value)) {
    assignUsersError.value = 'Assignments are blocked for closed/drawn/archived lotteries.'
    assignUsersLoading.value = false
    return
  }

  const eligible = selectedUserIds.value
    .map((id) => Number(id))
    .filter((id) => {
      const user = assignableUsers.value.find((candidate) => Number(candidate?.id) === id)
      return isUserAssignable(user)
    })

  if (!eligible.length) {
    assignUsersError.value = 'No eligible users selected for assignment.'
    assignUsersLoading.value = false
    return
  }

  if (!selectedUserIds.value.length) {
    assignUsersError.value = 'Select at least one user to assign.'
    assignUsersLoading.value = false
    return
  }

  eligible.forEach((id) => setAssignmentRowLocked(id, true))
  const result = await lotteryStore.assignUsersToLottery(lotteryId.value, eligible)

  if (!result.success) {
    assignUsersError.value = lifecycleAwareAssignmentError(result.error, 'Failed to assign users.')
  } else {
    assignUsersMessage.value = `Assigned ${eligible.length} user(s).`
    selectedUserIds.value = []
    await Promise.all([lotteryStore.fetchAssignments(lotteryId.value), usersStore.fetchUsers()])
  }

  eligible.forEach((id) => setAssignmentRowLocked(id, false))

  assignUsersLoading.value = false
}

const assignManagers = async () => {
  if (assignmentMutationActive.value) return
  assignManagersLoading.value = true
  assignManagersError.value = ''
  assignManagersMessage.value = ''

  if (!canAssignTicketsForLotteryStatus(lotteryStatus.value)) {
    assignManagersError.value = 'Assignments are blocked for closed/drawn/archived lotteries.'
    assignManagersLoading.value = false
    return
  }

  const eligible = selectedManagerIds.value
    .map((id) => Number(id))
    .filter((id) => {
      const user = assignableUsers.value.find((candidate) => Number(candidate?.id) === id)
      return isUserAssignable(user)
    })

  if (!eligible.length) {
    assignManagersError.value = 'No eligible managers selected for assignment.'
    assignManagersLoading.value = false
    return
  }

  if (!selectedManagerIds.value.length) {
    assignManagersError.value = 'Select at least one manager/admin to assign.'
    assignManagersLoading.value = false
    return
  }

  eligible.forEach((id) => setAssignmentRowLocked(id, true))
  const result = await lotteryStore.assignManagersToLottery(lotteryId.value, eligible)

  if (!result.success) {
    assignManagersError.value = lifecycleAwareAssignmentError(result.error, 'Failed to assign managers.')
  } else {
    assignManagersMessage.value = `Assigned ${eligible.length} manager(s).`
    selectedManagerIds.value = []
    await Promise.all([lotteryStore.fetchAssignments(lotteryId.value), usersStore.fetchUsers()])
  }

  eligible.forEach((id) => setAssignmentRowLocked(id, false))

  assignManagersLoading.value = false
}

onMounted(async () => {
  pageLoading.value = true
  await lotteryStore.fetchLotteryDetail(lotteryId.value)
  await usersStore.ensureUsersHydrated()
  await loadAttachments()

  if (isManagerOrAdmin.value && !lotteryStore.error) {
    await loadManagerData()
  } else if (!lotteryStore.error) {
    await loadLatestUserBatch()
  }

  exportHistory.value = getRecentExportHistory()

  pageLoading.value = false
})

watch(lotteryId, async (nextId, previousId) => {
  if (nextId === previousId) return

  pageLoading.value = true
  await lotteryStore.fetchLotteryDetail(lotteryId.value)
  await usersStore.ensureUsersHydrated()
  await loadAttachments()

  if (isManagerOrAdmin.value && !lotteryStore.error) {
    await loadManagerData()
  } else if (!lotteryStore.error) {
    await loadLatestUserBatch()
  }

  pageLoading.value = false
})

onBeforeUnmount(() => {
  releaseLatestUserBatchImage()
})

watch(
  () => orgStore.activeSubOrgId,
  async () => {
    selectedUserIds.value = []
    selectedManagerIds.value = []

    if (isManagerOrAdmin.value) {
      await usersStore.ensureUsersHydrated({ force: true })
    }
  },
)

watch([ticketFilter, filteredTicketRows], () => {
  ticketPage.value = 1
})

const handleBackClick = () => {
  router.push('/dashboard')
}

const hasLotteryDetail = computed(() => {
  const lottery = lotteryStore.currentLottery
  return Boolean(lottery && (lottery?.id || lottery?.lottery_id))
})
</script>

<template>
  <div class="lottery-detail-container">
    <div class="header">
      <button @click="handleBackClick" class="back-btn">← Back</button>
      <h1>
        {{ lotteryStore.currentLottery?.name || 'Lottery Detail' }}
      </h1>
      <LifecycleStatusBadge :status-meta="lotteryStatusMeta" />
    </div>

    <p v-if="lotteryReadOnly" class="readonly-banner">
      This lottery is in {{ lotteryStatus }} state and is read-only in the current workflow.
    </p>

    <div v-if="pageLoading" class="loading-skeleton-grid">
      <LotterySkeletonCard />
      <div class="panel">
        <Skeleton class="skeleton-block title" />
        <Skeleton class="skeleton-block" />
        <Skeleton class="skeleton-block short" />
      </div>
      <LotterySkeletonCard />
    </div>

    <div v-else-if="lotteryStore.error" class="error">
      {{ lotteryStore.error }}
    </div>

    <div v-else-if="!hasLotteryDetail" class="loading-inline">
      Lottery detail is unavailable right now. Please retry.
    </div>

    <template v-else>
      <!-- MANAGER / ADMIN VIEW -->
      <div v-if="isManagerOrAdmin" class="manager-view">
        <section class="lottery-info">
          <p><strong>Tickets per batch:</strong> {{ lotteryStore.currentLottery?.tickets_per_batch }}</p>
          <p><strong>Ticket price:</strong> {{ ticketPriceDisplay }}</p>
          <p><strong>Status:</strong> {{ lotteryStatusMeta.label }}</p>
          <p><strong>Total lottery value:</strong> {{ totalLotteryValueDisplay }}</p>
          <p v-if="lotteryStore.currentLottery?.deadline_at">
            <strong>Deadline:</strong> {{ formatDeadline(lotteryStore.currentLottery?.deadline_at) }}
          </p>
          <button class="btn-inline" type="button" :disabled="deadlineReminderLoading || lotteryReadOnly" @click="triggerDeadlineReminder">
            {{ deadlineReminderLoading ? 'Sending...' : 'Send deadline reminder' }}
          </button>
          <p v-if="deadlineReminderMessage" class="success">{{ deadlineReminderMessage }}</p>
          <p v-if="deadlineReminderError" class="error-inline">{{ deadlineReminderError }}</p>
        </section>

        <section class="attachments-section">
          <h2>Ticket Summary</h2>
          <div class="integrity-grid">
            <p><strong>Total tickets:</strong> {{ ticketSummary.total }}</p>
            <p><strong>Assigned tickets:</strong> {{ ticketSummary.assigned }}</p>
            <p><strong>Sold tickets:</strong> {{ ticketSummary.sold }}</p>
            <p><strong>Winners:</strong> {{ ticketSummary.winner }}</p>
            <p><strong>Ticket price:</strong> {{ ticketPriceDisplay }}</p>
            <p><strong>Total lottery value:</strong> {{ totalLotteryValueDisplay }}</p>
          </div>
        </section>

        <LotteryIntegritySection :warnings="structuredIntegrityWarnings" />

        <section class="assignments-section">
          <h2>Assignments</h2>
          <AssignmentTable :assignments="enrichedAssignments" :lottery-id="lotteryId" />
        </section>

        <section v-if="can.assignUsers" class="assign-section">
          <h2>Assign Users</h2>
          <p v-if="!selectedSubOrgId" class="error-inline">
            Select a sub-organization to view eligible users
          </p>

          <div class="picker">
            <label v-for="u in assignableUsers" :key="`user-${u.id}`" class="pick-item">
              <input
                type="checkbox"
                :checked="selectedUserIds.includes(Number(u.id))"
                :disabled="assignedIds.has(Number(u.id)) || !isUserAssignable(u) || isAssignmentRowLocked(u.id) || assignmentMutationActive"
                @change="toggleUserSelection(u.id, $event.target.checked)"
              />
              <span>{{ u.username }} (#{{ u.id }})<em v-if="!isUserAssignable(u)"> - unavailable</em></span>
            </label>
          </div>

          <button @click="assignUsers" :disabled="assignUsersLoading || !selectedSubOrgId || lotteryReadOnly">
            {{ assignUsersLoading ? 'Assigning...' : 'Assign selected users' }}
          </button>
          <p v-if="assignUsersMessage" class="success">{{ assignUsersMessage }}</p>
          <p v-if="assignUsersError" class="error-inline">{{ assignUsersError }}</p>
        </section>

        <section v-if="can.assignManagers" class="assign-section">
          <h2>Assign Managers</h2>
          <p v-if="!selectedSubOrgId" class="error-inline">
            Select a sub-organization to view eligible users
          </p>

          <div class="picker">
            <label v-for="u in assignableUsers" :key="`manager-${u.id}`" class="pick-item">
              <input
                type="checkbox"
                :checked="selectedManagerIds.includes(Number(u.id))"
                :disabled="assignedIds.has(Number(u.id)) || !isUserAssignable(u) || isAssignmentRowLocked(u.id) || assignmentMutationActive"
                @change="toggleManagerSelection(u.id, $event.target.checked)"
              />
              <span>{{ u.username }} (#{{ u.id }})<em v-if="!isUserAssignable(u)"> - unavailable</em></span>
            </label>
          </div>

          <button @click="assignManagers" :disabled="assignManagersLoading || !selectedSubOrgId || lotteryReadOnly">
            {{ assignManagersLoading ? 'Assigning...' : 'Assign selected managers' }}
          </button>
          <p v-if="assignManagersMessage" class="success">{{ assignManagersMessage }}</p>
          <p v-if="assignManagersError" class="error-inline">{{ assignManagersError }}</p>
        </section>

        <section v-if="can.drawWinner" class="draw-section">
          <h2>Draw</h2>
          <DrawPanel v-if="!lotteryReadOnly" :lottery-id="lotteryId" :assignments="lotteryStore.assignments" />
          <p v-else class="empty-inline">Draw execution is unavailable in read-only lifecycle states.</p>
        </section>

        <LotteryApiOpsPanel :lottery-id="lotteryId" :role="authStore.role" :is-super-admin="authStore.isSuperAdmin" />

        <section id="ticket-operations" class="attachments-section">
          <h2>Ticket Lifecycle Operations</h2>
          <div class="ticket-toolbar">
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy || !ticketLifecycleRows.length" @click="openExportDialog('tickets')">Export ownership CSV</button>
          </div>
          <div class="integrity-grid">
            <p><strong>Total tickets:</strong> {{ ticketSummary.total }}</p>
            <p><strong>Assigned:</strong> {{ ticketSummary.assigned }}</p>
            <p><strong>Sold:</strong> {{ ticketSummary.sold }}</p>
            <p><strong>Winners:</strong> {{ ticketSummary.winner }}</p>
          </div>

          <p v-if="ticketIntegrity.warnings.length" class="error-inline">
            {{ ticketIntegrity.warnings.join(' ') }}
          </p>

          <div class="ticket-toolbar" :class="{ sticky: selectedTicketNumbers.length > 0 }">
            <input
              v-model="ticketInputValue"
              class="form-control"
              type="text"
              placeholder="Ticket numbers (e.g. 1,2,10-15)"
              :disabled="ticketBulkBusy"
              @change="syncTicketSelectionFromInput"
            />
            <select v-model="ticketFilter" class="form-select" :disabled="ticketBulkBusy">
              <option value="all">All states</option>
              <option value="available">Available</option>
              <option value="assigned">Assigned</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
              <option value="winner">Winner</option>
            </select>
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy" @click="selectFilteredTickets">Select filtered</button>
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy" @click="clearTicketSelection">Clear</button>
            <select class="form-select" :value="String(ticketPageSize)" :disabled="ticketBulkBusy" @change="updateTicketPageSize($event.target.value)">
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>
          </div>

          <div class="ticket-toolbar responsive">
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy || !selectedTicketNumbers.length" @click="openTicketActionDialog('reserved')">Bulk reserve</button>
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy || !selectedTicketNumbers.length" @click="openTicketActionDialog('assigned')">Bulk release</button>
            <button class="btn-inline" type="button" :disabled="ticketBulkBusy || !selectedTicketNumbers.length" @click="openTicketActionDialog('sold')">Bulk sold</button>
          </div>

          <p v-if="ticketOperationMessage" class="success">{{ ticketOperationMessage }}</p>
          <p v-if="ticketOperationError" class="error-inline">{{ ticketOperationError }}</p>

          <table v-if="visibleTicketRows.length" class="history-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Ticket</th>
                <th>State</th>
                <th>Owner</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in visibleTicketRows" :key="`ticket-${row.ticketNumber}`">
                <td>
                  <input
                    type="checkbox"
                    :checked="selectedTicketSet.has(row.ticketNumber)"
                    :disabled="ticketBulkBusy"
                    @change="toggleTicketSelection(row.ticketNumber, $event.target.checked)"
                  />
                </td>
                <td>#{{ row.ticketNumber }}</td>
                <td>
                  <span class="execution-chip" :class="`ticket-tone-${getTicketLifecycleMeta(row.state).tone}`">
                    {{ getTicketLifecycleMeta(row.state).label }}
                  </span>
                </td>
                <td>{{ row.ownerUserId ? toTicketOwnerLabel(row.ownerUserId) : 'Unassigned' }}</td>
                <td>{{ row.history?.length || 0 }} event(s)</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty-inline">No ticket lifecycle rows available.</p>
          <footer v-if="filteredTicketRows.length" class="pagination-row">
            <button class="btn-inline" type="button" :disabled="ticketPage <= 1" @click="ticketPage -= 1">Previous</button>
            <span class="loading-inline">Page {{ paginatedTicketRows.page }} of {{ paginatedTicketRows.totalPages }} ({{ paginatedTicketRows.total }} tickets)</span>
            <button class="btn-inline" type="button" :disabled="ticketPage >= paginatedTicketRows.totalPages" @click="ticketPage += 1">Next</button>
          </footer>
        </section>

        <section class="attachments-section">
          <h2>Ticket History</h2>
          <p v-if="ticketOpsStore.loading" class="loading-inline">Loading ticket history...</p>
          <ul v-else-if="ticketOpsStore.ticketHistory.length" class="history-list">
            <li
              v-for="(event, index) in ticketOpsStore.ticketHistory"
              :key="event.id || `ticket-history-${index}`"
            >
              <strong>#{{ event.ticket_number ?? event.ticket ?? 'unknown' }}</strong>
              {{ event.from_state || 'unknown' }} -> {{ event.to_state || event.state || 'unknown' }}
              <span>({{ event.created_at || event.timestamp || 'Timestamp unavailable' }})</span>
            </li>
          </ul>
          <p v-else class="empty-inline">No ticket history entries found.</p>
        </section>
      </div>

      <!-- USER VIEW -->
      <div v-else class="user-view">
        <section class="lottery-info">
          <p><strong>Tickets per batch:</strong> {{ lotteryStore.currentLottery?.tickets_per_batch }}</p>
          <p><strong>Ticket price:</strong> {{ ticketPriceDisplay }}</p>
          <p><strong>Total lottery value:</strong> {{ totalLotteryValueDisplay }}</p>
          <p><strong>Status:</strong> {{ lotteryStatusMeta.label }}</p>
          <p v-if="lotteryStore.currentLottery?.deadline_at">
            <strong>Deadline:</strong> {{ formatDeadline(lotteryStore.currentLottery?.deadline_at) }}
          </p>
        </section>

        <section v-if="can.uploadBatch" class="ticket-section">
          <h2>Select Your Sold Tickets</h2>
          <div v-if="latestUserBatch" class="existing-batch-card">
            <p>
              You have already uploaded ticket proof for this lottery.
              Your latest submitted tickets are preselected below.
            </p>
            <p>
              <strong>Latest upload:</strong>
              {{ latestUserBatch.created_at }}
            </p>
            <div class="existing-image-wrap">
              <div v-if="latestUserBatchImageLoading" class="existing-image-placeholder">Loading uploaded image...</div>
              <img
                v-else-if="latestUserBatchImageUrl"
                :src="latestUserBatchImageUrl"
                alt="Uploaded ticket proof"
                class="existing-image"
              />
              <div v-else class="existing-image-placeholder">
                No uploaded image preview available.
                <span v-if="latestUserBatchImageError">{{ latestUserBatchImageError }}</span>
              </div>
            </div>
          </div>
          <TicketGrid
            :tickets-per-batch="lotteryStore.currentLottery?.tickets_per_batch || 100"
            :initial-selected-tickets="selectedTickets"
            @selected-tickets="onSelectedTickets"
          />
        </section>

        <section v-if="can.uploadBatch" class="upload-section">
          <h2>Upload Ticket Proof</h2>
          <p v-if="lotteryReadOnly" class="empty-inline">Ticket submission is disabled in read-only lifecycle states.</p>
          <UploadForm
            :lottery-id="lotteryId"
            :selected-tickets="selectedTickets"
            :tickets-per-batch="lotteryStore.currentLottery?.tickets_per_batch || 0"
            :ticket-price="lotteryStore.currentLottery?.ticket_price"
            :disabled="lotteryReadOnly"
            @upload-success="handleUserUploadSuccess"
          />
        </section>
      </div>

      <section class="attachments-section">
        <h2>Attachments</h2>

        <p v-if="attachmentsLoading" class="loading-inline">Loading attachments...</p>
        <p v-if="attachmentsError" class="error-inline">{{ attachmentsError }}</p>

        <div v-if="!attachmentsLoading && attachments.length" class="attachments-list">
          <div v-for="attachment in attachments" :key="getAttachmentId(attachment)" class="attachment-row">
            <div class="attachment-meta">
              <strong>{{ getAttachmentFilename(attachment) }}</strong>
              <span>{{ getAttachmentCreatedAt(attachment) || 'Unknown date' }}</span>
              <span>Uploaded by: {{ getAttachmentUploadedBy(attachment) }}</span>
            </div>
            <button class="btn-inline" type="button" @click="downloadAttachment(attachment)">Download</button>
          </div>
        </div>

        <p v-else-if="!attachmentsLoading" class="empty-inline">No attachments uploaded.</p>

        <div v-if="isManagerOrAdmin" class="upload-wrap">
          <h3>Upload attachment</h3>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            :disabled="attachmentUploadLoading || lotteryReadOnly"
            @change="onAttachmentFileSelected"
          />
          <label class="checkbox-row">
            <input v-model="replaceExistingAttachment" type="checkbox" :disabled="attachmentUploadLoading || lotteryReadOnly" />
            Replace existing
          </label>
          <button class="btn-inline" type="button" :disabled="attachmentUploadLoading || lotteryReadOnly" @click="uploadAttachment">
            {{ attachmentUploadLoading ? 'Uploading...' : 'Upload' }}
          </button>
          <p v-if="attachmentUploadSuccess" class="success">{{ attachmentUploadSuccess }}</p>
          <p v-if="attachmentUploadError" class="error-inline">{{ attachmentUploadError }}</p>
        </div>
      </section>


      <section id="execution-history" class="attachments-section">
        <h2>Execution History</h2>
        <button class="btn-inline" type="button" :disabled="!executionTimeline.length" @click="openExportDialog('execution')">Export execution CSV</button>
        <div v-if="executionTimeline.length" class="execution-timeline">
          <article
            v-for="entry in executionTimeline"
            :key="entry.id || `${entry.executedAt}-${entry.statusLabel}`"
            class="execution-item"
            :class="{ latest: entry.isLatest, recoverable: entry.isRecoverable }"
          >
            <p>
              <strong>{{ entry.isLatest ? 'Latest execution' : 'Execution' }}</strong>
              <span v-if="entry.executedAt"> - {{ entry.executedAt }}</span>
            </p>
            <p v-if="entry.statusLabel"><strong>Status:</strong> {{ entry.statusLabel }}</p>
            <p v-if="entry.winnerCount !== null"><strong>Winner count:</strong> {{ entry.winnerCount }}</p>
            <p v-if="entry.executionNotes"><strong>Notes:</strong> {{ entry.executionNotes }}</p>
          </article>
        </div>
        <p v-else class="empty-inline">No draw execution history has been recorded yet.</p>
      </section>

      <section id="execution-integrity" class="attachments-section">
        <h2>Draw Result Integrity</h2>
        <div v-if="latestExecutionIntegrity" class="integrity-grid">
          <p v-for="signal in integritySignals" :key="signal.key" :class="`integrity-${signal.state}`"><strong>{{ signal.label }}:</strong> {{ signal.value }}</p>
          <p><strong>Randomization metadata:</strong> {{ latestExecutionIntegrity.randomizationMetadata || 'Not provided' }}</p>
          <p><strong>Execution metadata:</strong> {{ latestExecutionIntegrity.executionMetadata || 'Not provided' }}</p>
        </div>
        <p v-else class="empty-inline">Integrity metadata will appear after the first draw execution.</p>
      </section>

      <section id="draw-outcomes" class="attachments-section">
        <h2>Draw Outcomes</h2>
        <button class="btn-inline" type="button" :disabled="!latestWinnerRows.length" @click="openExportDialog('winners')">Export winners CSV</button>
        <table v-if="latestWinnerRows.length" class="history-table">
          <thead>
            <tr>
              <th>Winner</th>
              <th>Ticket</th>
              <th>Validation</th>
              <th>Lifecycle</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="winner in latestWinnerRows"
              :key="winner.key"
              :class="{ 'winner-duplicate': winner.duplicate, 'winner-invalid': winner.invalid }"
            >
              <td>{{ toWinnerDisplayName(winner) }}</td>
              <td>{{ toTicketDisplayValue(winner.ticketNumber) }}</td>
              <td>{{ winner.duplicate ? 'Duplicate ticket' : winner.invalid ? 'Incomplete winner record' : 'OK' }}</td>
              <td><LifecycleStatusBadge :status-meta="winnerLifecycleMeta(winner)" /></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-inline">No winner records available yet.</p>
      </section>

      <AdminActionDialog
        :open="Boolean(pendingTicketAction)"
        :title="ticketActionTitle"
        :message="ticketActionMessage"
        :details="['Bulk operations are replay-protected and may return partial failures per ticket.']"
        :tone="ticketActionTone"
        confirm-label="Execute"
        :loading="pendingTicketActionLoading"
        loading-label="Applying ticket updates..."
        @close="closeTicketActionDialog"
        @confirm="confirmTicketAction"
      />

      <section class="attachments-section">
        <h2>Recent Exports</h2>
        <ul v-if="exportHistory.length" class="history-list">
          <li v-for="entry in exportHistory" :key="entry.id">
            {{ entry.export_scope }} - {{ entry.record_count }} rows - {{ entry.exported_at }}
            <span v-if="entry.partial_export_warning">(partial warning)</span>
          </li>
        </ul>
        <p v-else class="empty-inline">No exports recorded yet.</p>
      </section>

      <AdminActionDialog
        :open="exportDialogOpen"
        title="Export Lottery Report"
        :message="`Export scope: ${exportScope}.`"
        :details="[
          'CSV export includes currently available data for this scope.',
          exportRowsByScope.length ? `Records to export: ${exportRowsByScope.length}` : 'No records available for export.',
        ]"
        tone="warning"
        confirm-label="Export CSV"
        :loading="exportLoading"
        loading-label="Preparing export..."
        @close="closeExportDialog"
        @confirm="exportDetailCsv"
      />

      <p v-if="exportError" class="error-inline">{{ exportError }}</p>
    </template>
  </div>
</template>

<style scoped>
.lottery-detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.readonly-banner {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.65rem;
}

.back-btn {
  padding: 0.5rem 1rem;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  color: #111827;
}

.back-btn:hover {
  background-color: #eee;
}

h1 {
  color: #111827;
}

h2 {
  color: #111827;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

section {
  margin-bottom: 3rem;
  padding: 1.5rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.error {
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
}

.error {
  background-color: #ffebee;
  color: #c62828;
}

.manager-view,
.user-view {
  display: grid;
  gap: 2rem;
}

.assign-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.picker {
  display: grid;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.pick-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
}

.success {
  color: #166534;
  background: #dcfce7;
  padding: 0.5rem;
  border-radius: 6px;
}

.error-inline {
  color: #991b1b;
  background: #fee2e2;
  padding: 0.5rem;
  border-radius: 6px;
}

.existing-batch-card {
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1f2937;
}

.existing-image-wrap {
  margin-top: 0.75rem;
}

.existing-image {
  max-width: 100%;
  max-height: 260px;
  object-fit: contain;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
}

.existing-image-placeholder {
  padding: 1rem;
  border: 1px dashed #93c5fd;
  border-radius: 8px;
  color: #1d4ed8;
  background: #fff;
}

.attachments-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.attachments-list {
  display: grid;
  gap: 0.5rem;
}

.history-list {
  margin: 0;
  padding-left: 1.1rem;
  color: #374151;
}

.execution-timeline {
  display: grid;
  gap: 0.7rem;
}

.execution-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  background: #f9fafb;
  color: #1f2937;
}

.execution-item.latest {
  border-color: #93c5fd;
  background: #eff6ff;
}

.execution-item.recoverable {
  border-color: #fcd34d;
}

.integrity-grid {
  display: grid;
  gap: 0.4rem;
  color: #1f2937;
}

.integrity-missing {
  color: #92400e;
}

.integrity-ok {
  color: #166534;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table th,
.history-table td {
  border: 1px solid #e5e7eb;
  padding: 0.45rem;
  text-align: left;
  color: #1f2937;
}

.winner-duplicate {
  background: #fff7ed;
}

.winner-invalid {
  background: #fef2f2;
}

.attachment-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.attachment-meta {
  display: grid;
  gap: 0.2rem;
  color: #1f2937;
}

.btn-inline {
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  padding: 0.45rem 0.7rem;
  cursor: pointer;
}

.ticket-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.75rem 0;
}

.ticket-toolbar.sticky {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.5rem;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #eff6ff;
}

.ticket-toolbar.responsive {
  margin-top: 0.25rem;
}

.form-control,
.form-select {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  min-width: 220px;
}

.execution-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.82rem;
  border: 1px solid transparent;
}

.ticket-tone-neutral {
  color: #374151;
  background: #f3f4f6;
  border-color: #d1d5db;
}

.ticket-tone-active {
  color: #1d4ed8;
  background: #dbeafe;
  border-color: #93c5fd;
}

.ticket-tone-warning {
  color: #92400e;
  background: #fffbeb;
  border-color: #fcd34d;
}

.ticket-tone-success {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}

.ticket-tone-danger,
.ticket-tone-deleted {
  color: #991b1b;
  background: #fee2e2;
  border-color: #fca5a5;
}

.upload-wrap {
  margin-top: 1rem;
  padding-top: 0.8rem;
  border-top: 1px solid #e5e7eb;
  display: grid;
  gap: 0.45rem;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #111827;
}

.loading-inline,
.empty-inline {
  color: #6b7280;
}

.loading-skeleton-grid {
  display: grid;
  gap: 1rem;
}

.pagination-row {
  margin-top: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.skeleton-block {
  height: 14px;
  margin-bottom: 10px;
}

.title {
  width: 70%;
  height: 18px;
}

.short {
  width: 45%;
}

@media (max-width: 768px) {
  .ticket-toolbar {
    display: grid;
    grid-template-columns: 1fr;
  }

  .form-control,
  .form-select {
    min-width: 0;
    width: 100%;
  }
}
</style>

